/*
 * Category chips on the collection page.
 *
 * The chips filter on the feature labels klydo-api writes to each collection product
 * (custom.N_custom_text — "Chime", "Sound", "Reminder"), the same values the product page
 * shows in its icon row. snippets/collection-card-item.liquid puts them on every card as
 * data-features.
 *
 * The grid paginates at 24, so filtering what is on screen would only ever search one
 * page. The rest of the collection is pulled through the section rendering API, cached per
 * request (sort order and any active facets), and the matching cards are shown together
 * with the pager hidden.
 *
 * Back from a product reloads this page (the bfcache does not keep it), so the filtered
 * grid is saved to sessionStorage when the page is left and restored from there on Back
 * only. Every other load fetches fresh cards.
 *
 * The selected chip is held here rather than read back from the URL: facets.js rebuilds
 * the query string from the sort form alone when the sort changes, which drops ?feature=
 * before the grid is swapped.
 */
(() => {
  const FEATURE_PARAM = 'feature';
  const ACTIVE_CLASS = 'collection-pills__pill--active';
  const EMPTY_CLASS = 'collection-pills__empty';
  /* One section render per page is a heavy response; a few at a time is plenty. */
  const MAX_PARALLEL_REQUESTS = 4;
  /* A stalled request would otherwise leave the grid dimmed and busy indefinitely. */
  const REQUEST_TIMEOUT = 15000;
  /* One entry, overwritten each time the page is left, so it never grows with the scopes. */
  const STORAGE_KEY = 'collection-category-pills';
  const STORED_CARDS_LIFETIME = 10 * 60 * 1000;

  const pillContainer = document.querySelector('.collection-pills');
  if (!pillContainer) return;

  let activeFeature = new URLSearchParams(window.location.search).get(FEATURE_PARAM) || '';
  let unfilteredGridHtml = null;
  let cachedCards = null;
  let cachedKey = null;
  let isApplying = false;
  /* Guards against a slow earlier request repainting the grid after a later chip won. */
  let latestRequest = 0;

  const getGrid = () => document.getElementById('product-grid');
  const getPager = () => document.querySelector('.pagination-wrapper');
  const getSortValue = () => document.getElementById('SortBy')?.value ?? '';
  const getCountElements = () =>
    [document.getElementById('ProductCount'), document.getElementById('ProductCountDesktop')].filter(
      Boolean,
    );

  /* Everything the collection is currently scoped by — sort, facets, search terms — minus
     our own parameter and the page. Kept so a filtered view respects them, and so the
     cache is not shared between two different scopes. */
  const scopeParams = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete(FEATURE_PARAM);
    params.delete('page');
    const sortValue = getSortValue();
    if (sortValue) params.set('sort_by', sortValue);
    return params;
  };

  const storedCardsKey = (feature) => `${window.location.pathname}?${scopeParams()}#${feature}`;

  /* Storage can be full, disabled or blocked; any failure just means fetching instead. */
  const readStoredCards = (feature) => {
    try {
      const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      if (stored?.key !== storedCardsKey(feature)) return null;
      if (Date.now() - (stored.savedAt ?? 0) > STORED_CARDS_LIFETIME) return null;
      const template = document.createElement('template');
      template.innerHTML = stored.html;
      return [...template.content.children];
    } catch {
      return null;
    }
  };

  /* Written once on leaving rather than on every chip: only the last one is ever read. */
  const storeCards = (feature, cards) => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          key: storedCardsKey(feature),
          savedAt: Date.now(),
          html: cards.map((card) => card.outerHTML).join(''),
        }),
      );
    } catch {
      /* Not stored; Back will fetch. */
    }
  };

  const fetchAllCards = async () => {
    const grid = getGrid();
    const totalPages = Number(grid.dataset.totalPages || 1);
    const key = scopeParams().toString();
    if (cachedCards && cachedKey === key) return cachedCards;

    const requestPage = async (page) => {
      const params = scopeParams();
      params.set('section_id', grid.dataset.id);
      params.set('page', String(page));
      const response = await fetch(`${window.location.pathname}?${params}`, {
        signal: AbortSignal.timeout(REQUEST_TIMEOUT),
      });
      if (!response.ok) throw new Error(`Section render failed for page ${page}: ${response.status}`);
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      return [...parsed.querySelectorAll('#product-grid > .grid__item')];
    };

    const cards = [];
    for (let page = 1; page <= totalPages; page += MAX_PARALLEL_REQUESTS) {
      const batch = [];
      for (let offset = 0; offset < MAX_PARALLEL_REQUESTS && page + offset <= totalPages; offset += 1) {
        batch.push(requestPage(page + offset));
      }
      /* Any failure aborts: a short list must never be cached as the whole collection. */
      cards.push(...(await Promise.all(batch)).flat());
    }

    cachedCards = cards;
    cachedKey = key;
    return cards;
  };

  const matchesFeature = (card, feature) =>
    (card.dataset.features || '')
      .split('|')
      .some((label) => label.trim().toLowerCase() === feature.toLowerCase());

  const markActivePill = (feature) => {
    pillContainer.querySelectorAll('[data-category-pill]').forEach((pill) => {
      const isActive = (pill.dataset.categoryPill || '').toLowerCase() === feature.toLowerCase();
      pill.classList.toggle(ACTIVE_CLASS, isActive);
      if (isActive) {
        pill.setAttribute('aria-current', 'page');
      } else {
        pill.removeAttribute('aria-current');
      }
    });
  };

  const renderEmptyState = (grid) => {
    const message = grid.dataset.emptyLabel;
    if (!message) return;
    const item = document.createElement('li');
    item.className = EMPTY_CLASS;
    item.textContent = message;
    grid.appendChild(item);
  };

  const hideUnfilteredCounters = () => {
    const pager = getPager();
    if (pager) pager.hidden = true;
    getCountElements().forEach((element) => {
      element.hidden = true;
    });
  };

  /* Clearing a chip hands the grid back to facets.js rather than restoring markup this
     script captured: after a sort change under a chip that markup is in the previous
     order, and it has no pagination for the full collection. Dispatching the event
     facets.js listens for makes it render page one of the current sort itself. activeFeature
     is already empty by this point, so the interceptor above lets the event through. */
  const showUnfiltered = () => {
    const pager = getPager();
    if (pager) pager.hidden = false;
    getCountElements().forEach((element) => {
      element.hidden = false;
    });
    const sortForm = document.getElementById('FacetFiltersForm');
    if (sortForm) {
      sortForm.dispatchEvent(new Event('input', { bubbles: true }));
    } else if (unfilteredGridHtml !== null) {
      getGrid().innerHTML = unfilteredGridHtml;
      writeUrl('', true);
    }
  };

  const applyFeature = async (feature, useStoredCards = false) => {
    const grid = getGrid();
    if (!grid) return;
    /* The empty-collection branch of the section renders a plain div with no pagination
       data and no empty-state label; filtering it would only wipe its "no products"
       message. */
    if (!grid.dataset.totalPages) return;

    activeFeature = feature;
    if (unfilteredGridHtml === null) unfilteredGridHtml = grid.innerHTML;
    markActivePill(feature);

    if (!feature) {
      /* Bumped so a chip fetch still in flight cannot come back and repaint over the
         unfiltered grid, and cleared so the observer stops ignoring facets.js renders. */
      latestRequest += 1;
      isApplying = false;
      grid.removeAttribute('aria-busy');
      grid.closest('.collection')?.classList.remove('loading');
      showUnfiltered();
      return;
    }

    const requestId = latestRequest + 1;
    latestRequest = requestId;
    isApplying = true;
    grid.setAttribute('aria-busy', 'true');

    /* Whatever is on screen stays there, dimmed by the section's loading overlay — the
       same one facets.js uses — until the single paint below replaces it. Nothing is
       emptied and nothing is painted twice. */
    const collectionElement = grid.closest('.collection');
    collectionElement?.classList.add('loading');

    /* facets.js has just rendered a fresh pager and product count for the unfiltered
       collection; both belong to that, not to the filtered set, so they go before the
       fetch rather than after it. */
    hideUnfilteredCounters();

    try {
      const storedCards = useStoredCards ? readStoredCards(feature) : null;
      const matching =
        storedCards ?? (await fetchAllCards()).filter((card) => matchesFeature(card, feature));
      /* A newer chip took over while this was in flight. */
      if (requestId !== latestRequest) return;

      grid.innerHTML = '';
      matching.forEach((card) => {
        const placed = card.cloneNode(true);
        /* Each card carries the --animation-order of the page it was fetched from, which
           base.css turns into an animation-delay — so the reveal ran in fetch order and
           rows appeared out of sequence. facets.js cancels the animation on anything it
           re-renders for the same reason; the cards appear together instead. */
        placed.classList.add('scroll-trigger--cancel');
        placed
          .querySelectorAll('.scroll-trigger')
          .forEach((element) => element.classList.add('scroll-trigger--cancel'));
        placed.style.removeProperty('--animation-order');
        grid.appendChild(placed);
      });
      if (matching.length === 0) renderEmptyState(grid);
      /* Again, in case facets.js replaced either of them while the pages were loading. */
      hideUnfilteredCounters();
    } catch (error) {
      console.warn('[collection-category-pills]', error);
      if (requestId === latestRequest) {
        /* Never leave a partial collection on screen pretending to be the filtered set.
           The chip is cleared first: showUnfiltered hands the grid back to facets.js with
           the event the interceptor below swallows while a feature is still set. */
        activeFeature = '';
        markActivePill('');
        showUnfiltered();
      }
    } finally {
      if (requestId === latestRequest) {
        collectionElement?.classList.remove('loading');
        grid.removeAttribute('aria-busy');
        isApplying = false;
      }
    }
  };

  /* facets.js reads event.state.searchParams on every popstate, so the key has to be there
     or it renders the grid from `undefined`. The sort is written here too: while a chip is
     active facets.js never runs, so nothing else would put the new sort in the URL. */
  function writeUrl(feature, replace = false) {
    const params = new URLSearchParams(window.location.search);
    if (feature) {
      params.set(FEATURE_PARAM, feature);
    } else {
      params.delete(FEATURE_PARAM);
    }
    const sortValue = getSortValue();
    if (sortValue) params.set('sort_by', sortValue);
    /* The filtered view is the whole collection, so a page from the unfiltered paging
       would only mislead on reload. */
    params.delete('page');
    const query = params.toString();
    const searchParams = scopeParams().toString();
    const url = `${window.location.pathname}${query && `?${query}`}`;
    if (replace) history.replaceState({ feature, searchParams }, '', url);
    else history.pushState({ feature, searchParams }, '', url);
  }

  /* While a chip is active facets.js must not render the sort: it would paint the
     unfiltered collection for the new order and the chip would then paint over it — the
     cards rendering twice. Its listeners sit on the facets forms, so a capture-phase
     listener here runs first and stops the event reaching them; the re-filter below is
     then the only paint. With no chip active the event is left alone.

     Matched on the sort control itself rather than the form: the same form also holds the
     facet inputs when filtering is on, and swallowing those would leave a checkbox ticked
     with the results unchanged. Keying on the name covers the desktop, mobile and drawer
     sort forms alike. */
  document.addEventListener(
    'input',
    (event) => {
      if (!activeFeature) return;
      if (event.target.name !== 'sort_by') return;
      event.stopImmediatePropagation();
      writeUrl(activeFeature);
      /* The sort is part of the cache key's scope, so the pages have to be refetched. */
      cachedCards = null;
      cachedKey = null;
      applyFeature(activeFeature);
    },
    true,
  );

  pillContainer.addEventListener('click', (event) => {
    const pill = event.target.closest('[data-category-pill]');
    if (!pill) return;
    /* The chips are real links; a modified or middle click should still open the filtered
       view in a new tab or window rather than being handled here. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    const feature = pill.dataset.categoryPill || '';
    /* Only pushed for a chip: clearing hands the grid to facets.js, which writes its own
       entry — pushing here too would need two Back presses to get out of the filter. */
    if (feature) writeUrl(feature);
    applyFeature(feature);
  });

  window.addEventListener('popstate', (event) => {
    const feature = event.state?.feature ?? new URLSearchParams(window.location.search).get(FEATURE_PARAM) ?? '';
    if (feature === activeFeature) return;
    applyFeature(feature);
  });

  /* facets.js replaces the innerHTML of #ProductGridContainer when the sort changes, so
     the captured markup and the cached pages both belong to the previous scope. Watching
     only its direct children keeps this from firing on our own swaps inside #product-grid. */
  const gridContainer = document.getElementById('ProductGridContainer');
  if (gridContainer) {
    new MutationObserver(() => {
      if (isApplying) return;
      unfilteredGridHtml = null;
      cachedCards = null;
      cachedKey = null;
      if (activeFeature) {
        /* Replaced, not pushed: facets.js has already added an entry for this render. */
        writeUrl(activeFeature, true);
        applyFeature(activeFeature);
      }
    }).observe(gridContainer, { childList: true });
  }

  window.addEventListener('pagehide', () => {
    const grid = getGrid();
    if (!grid || !activeFeature || isApplying) return;
    storeCards(activeFeature, [...grid.querySelectorAll(':scope > .grid__item')]);
  });

  if (activeFeature) {
    const isBackNavigation =
      performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
    applyFeature(activeFeature, isBackNavigation);
  } else {
    markActivePill('');
  }
})();
